import { useState } from 'react';
import { MODES, RETRY_INTERVAL_MS, WARN_EVERY } from './config';
import { shortId } from './engine';
import { clearLog, writeMode } from './storage';
import { useAutoContinueState } from './useAutoContinue';

const MODE_OPTIONS = [
  { value: MODES.OFF, label: "O'chiq", hint: "Hech narsa ishlamaydi, tekshiruv ham yo'q." },
  { value: MODES.DRY, label: 'Sinov', hint: "Tekshiruvlar ishlaydi, lekin continue yuborilmaydi — faqat jurnalga yoziladi." },
  { value: MODES.ON, label: 'Yoqilgan', hint: `Failed videolar har ${RETRY_INTERVAL_MS / 60000} daqiqada avtomatik davom ettiriladi — cheklovsiz; har ${WARN_EVERY} urinishda ogohlantirish.` },
];

const MODE_LABELS = { off: "O'chiq", dry: 'Sinov', on: 'Yoqilgan' };

const ACTION_LABELS = {
  continue: 'continue yuborildi',
  dry: 'sinov (yuborilmadi)',
  skip: "o'tkazib yuborildi",
  error: 'continue xatosi',
  warn: 'ogohlantirish',
};

const ACTION_COLORS = {
  continue: '#16a34a',
  dry: '#4338ca',
  skip: '#64748b',
  error: '#dc2626',
  warn: '#a16207',
};

const formatTime = (ms) =>
  new Date(ms).toLocaleString('uz-UZ', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });

export default function AutoContinuePanel() {
  const { mode, log } = useAutoContinueState();
  const [open, setOpen] = useState(false);
  const activeHint = MODE_OPTIONS.find((option) => option.value === mode)?.hint;

  return (
    <div style={styles.panel} data-testid="auto-continue-panel">
      <div style={styles.topRow}>
        <div>
          <div style={styles.title}>Failed videolarni avto-davom ettirish</div>
          <div style={styles.hint}>{activeHint}</div>
        </div>
        <div role="radiogroup" aria-label="Avto-davom rejimi" style={styles.segmented}>
          {MODE_OPTIONS.map((option) => {
            const active = option.value === mode;
            return (
              <button
                key={option.value}
                type="button"
                role="radio"
                aria-checked={active}
                onClick={() => writeMode(option.value)}
                style={active ? { ...styles.segment, ...styles.segmentActive, ...(option.value === MODES.ON ? styles.segmentOn : {}) } : styles.segment}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      </div>

      <div style={styles.logHeader}>
        <button type="button" style={styles.linkBtn} onClick={() => setOpen(!open)} aria-expanded={open}>
          {open ? '▾' : '▸'} Avto-davom jurnali ({log.length})
        </button>
        {open && log.length > 0 && (
          <button type="button" style={styles.clearBtn} onClick={clearLog}>
            Jurnalni tozalash
          </button>
        )}
      </div>

      {open && (
        log.length === 0 ? (
          <div style={styles.empty}>Jurnal bo'sh</div>
        ) : (
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Vaqt</th>
                  <th style={styles.th}>Task</th>
                  <th style={styles.th}>Rejim</th>
                  <th style={styles.th}>Harakat</th>
                  <th style={styles.th}>Xato matni</th>
                </tr>
              </thead>
              <tbody>
                {log.map((entry, index) => (
                  <tr key={`${entry.at}-${entry.taskId}-${index}`}>
                    <td style={styles.td}>{formatTime(entry.at)}</td>
                    <td style={{ ...styles.td, fontFamily: 'monospace' }} title={entry.taskId}>{shortId(entry.taskId)}</td>
                    <td style={styles.td}>{MODE_LABELS[entry.mode] || entry.mode}</td>
                    <td style={{ ...styles.td, color: ACTION_COLORS[entry.action] || '#334155', fontWeight: 500 }}>
                      {ACTION_LABELS[entry.action] || entry.action}
                      {entry.reason ? ` (${entry.reason})` : ''}
                    </td>
                    <td style={{ ...styles.td, color: '#64748b' }} title={entry.error}>{entry.error || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </div>
  );
}

const styles = {
  panel: {
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 12,
    padding: '14px 16px',
    marginBottom: 20,
    textAlign: 'left',
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  topRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    flexWrap: 'wrap',
  },
  title: { fontSize: 14, fontWeight: 600, color: '#0f172a' },
  hint: { fontSize: 12, color: '#64748b', marginTop: 2 },
  segmented: {
    display: 'inline-flex',
    padding: 3,
    gap: 2,
    background: '#f1f5f9',
    borderRadius: 999,
  },
  segment: {
    padding: '6px 14px',
    borderRadius: 999,
    border: 'none',
    background: 'transparent',
    color: '#475569',
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
  },
  segmentActive: {
    background: '#fff',
    color: '#4338ca',
    fontWeight: 600,
    boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
  },
  segmentOn: { background: '#6366f1', color: '#fff' },
  logHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 },
  linkBtn: {
    border: 'none',
    background: 'transparent',
    padding: 0,
    color: '#475569',
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
  },
  clearBtn: {
    padding: '4px 10px',
    borderRadius: 6,
    border: '1px solid #e2e8f0',
    background: '#fff',
    color: '#475569',
    fontSize: 12,
    cursor: 'pointer',
  },
  empty: { fontSize: 13, color: '#94a3b8' },
  tableWrap: { overflowX: 'auto', maxHeight: 280, overflowY: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 12 },
  th: {
    textAlign: 'left',
    padding: '6px 8px',
    color: '#94a3b8',
    fontWeight: 500,
    borderBottom: '1px solid #e2e8f0',
    whiteSpace: 'nowrap',
  },
  td: {
    padding: '6px 8px',
    borderBottom: '1px solid #f1f5f9',
    color: '#334155',
    verticalAlign: 'top',
    maxWidth: 320,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
};
